/**
 * TenantConfigContext
 *
 * OEM/ホワイトラベル対応のためのテナント設定 Context
 * - 認証前: tenant-config API（public）から brand/theme を取得
 * - 認証後: bootstrap API から全設定を取得
 *
 * Related: OEM-BASE-03
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TenantBrand {
  name: string;
  tagline?: string | null;
  logo_url?: string | null;
  favicon_url?: string | null;
  og_image_url?: string | null;
}

export interface TenantTheme {
  primary_color: string;
  secondary_color?: string | null;
  font_family?: string | null;
}

export interface TenantFeatureFlags {
  payment_enabled: boolean;
  line_enabled: boolean;
  vision_api_enabled: boolean;
  human_escalation_enabled: boolean;
  analytics_enabled: boolean;
}

export interface TenantSupport {
  email?: string | null;
  company_name?: string | null;
  help_url?: string | null;
}

export interface TenantLegal {
  terms_url?: string | null;
  privacy_url?: string | null;
}

export interface TenantConfig {
  tenant_key: string;
  brand: TenantBrand;
  theme: TenantTheme;
  domain?: string | null;
  feature_flags?: TenantFeatureFlags;
  support?: TenantSupport;
  legal?: TenantLegal;
}

interface TenantConfigContextValue {
  config: TenantConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default values
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_BRAND: TenantBrand = {
  name: "JARVIS LMS",
  tagline: "AI搭載学習プラットフォーム",
};

const DEFAULT_THEME: TenantTheme = {
  primary_color: "#3B82F6",
};

const DEFAULT_CONFIG: TenantConfig = {
  tenant_key: "default",
  brand: DEFAULT_BRAND,
  theme: DEFAULT_THEME,
};

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const TenantConfigContext = createContext<TenantConfigContextValue>({
  config: null,
  loading: true,
  error: null,
  refetch: async () => {},
});

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

interface TenantConfigProviderProps {
  children: ReactNode;
}

export function TenantConfigProvider({ children }: TenantConfigProviderProps) {
  const { user, loading: authLoading } = useAuth();
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.DEV ? "http://localhost:8000" : "");

  // Fetch public tenant config (no auth required)
  const fetchPublicConfig = useCallback(async () => {
    try {
      const host = window.location.hostname;
      const response = await fetch(
        `${apiBaseUrl}/api/v1/public/tenant-config?host=${encodeURIComponent(host)}`
      );

      if (!response.ok) {
        // Fallback to default if tenant not found
        if (response.status === 404) {
          setConfig(DEFAULT_CONFIG);
          return;
        }
        throw new Error(`Failed to fetch tenant config: ${response.status}`);
      }

      const data = await response.json();
      setConfig(data);
    } catch (err) {
      console.warn("Failed to fetch public tenant config, using defaults:", err);
      setConfig(DEFAULT_CONFIG);
    }
  }, [apiBaseUrl]);

  // Fetch full bootstrap config (auth required)
  const fetchBootstrapConfig = useCallback(async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        // No token, fall back to public config
        await fetchPublicConfig();
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/bootstrap`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bootstrap config: ${response.status}`);
      }

      const data = await response.json();
      setConfig(data);
    } catch (err) {
      console.warn("Failed to fetch bootstrap config, falling back to public:", err);
      await fetchPublicConfig();
    }
  }, [apiBaseUrl, fetchPublicConfig]);

  // Main fetch logic
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (user) {
        // Authenticated: use bootstrap API
        await fetchBootstrapConfig();
      } else {
        // Not authenticated: use public API
        await fetchPublicConfig();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [user, fetchBootstrapConfig, fetchPublicConfig]);

  // Refetch function for manual refresh
  const refetch = useCallback(async () => {
    await fetchConfig();
  }, [fetchConfig]);

  // Fetch config when auth state changes
  useEffect(() => {
    if (!authLoading) {
      fetchConfig();
    }
  }, [authLoading, user, fetchConfig]);

  return (
    <TenantConfigContext.Provider value={{ config, loading, error, refetch }}>
      {children}
    </TenantConfigContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useTenantConfig(): TenantConfigContextValue {
  const context = useContext(TenantConfigContext);
  if (!context) {
    throw new Error("useTenantConfig must be used within a TenantConfigProvider");
  }
  return context;
}

// Convenience hooks for specific parts of the config
export function useBrand(): TenantBrand {
  const { config } = useTenantConfig();
  return config?.brand ?? DEFAULT_BRAND;
}

export function useTheme(): TenantTheme {
  const { config } = useTenantConfig();
  return config?.theme ?? DEFAULT_THEME;
}

export function useFeatureFlags(): TenantFeatureFlags | undefined {
  const { config } = useTenantConfig();
  return config?.feature_flags;
}

export function useSupport(): TenantSupport | undefined {
  const { config } = useTenantConfig();
  return config?.support;
}

export function useLegal(): TenantLegal | undefined {
  const { config } = useTenantConfig();
  return config?.legal;
}
